import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, Download, Plus, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contracts = () => {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  
  // Search and filter states for contracts
  const [contractSearch, setContractSearch] = useState('');
  const [contractDateFilter, setContractDateFilter] = useState('');
  const [contractModelFilter, setContractModelFilter] = useState('');
  
  // Search and filter states for submissions (when creating contract)
  const [submissionSearch, setSubmissionSearch] = useState('');
  const [submissionDateFilter, setSubmissionDateFilter] = useState('');
  const [submissionModelFilter, setSubmissionModelFilter] = useState('');

  useEffect(() => {
    const initializePage = async () => {
      console.log('DEBUG: Contracts.jsx useEffect running - FIXED VERSION');
      const token = localStorage.getItem('admin_token');
      console.log('Token trouvé:', token ? 'Oui' : 'Non');
      
      if (!token) {
        console.log('Pas de token, redirection vers login');
        navigate('/login');
        return;
      }
      
      await fetchData();
    };
    
    initializePage();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      console.log('Chargement des contrats et soumissions...');
      
      const [contractsRes, submissionsRes] = await Promise.all([
        axios.get(`${API}/contracts?limit=200`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/submissions?limit=200`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      // Gérer le nouveau format paginé
      const contractsData = contractsRes.data.contracts || contractsRes.data;
      const submissionsData = submissionsRes.data.submissions || submissionsRes.data;
      
      console.log('Données chargées:', Array.isArray(contractsData) ? contractsData.length : 0, 'contrats,', Array.isArray(submissionsData) ? submissionsData.length : 0, 'soumissions');
      
      setContracts(Array.isArray(contractsData) ? contractsData : []);
      setSubmissions(Array.isArray(submissionsData) ? submissionsData : []);
    } catch (error) {
      console.error('Erreur fetchData:', error);
      console.error('Status:', error.response?.status);
      console.error('Message:', error.response?.data);
      
      // Si erreur d'authentification, rediriger vers login
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        localStorage.removeItem('admin_token');
        toast.error('Session expirée, veuillez vous reconnecter');
        navigate('/login');
        return;
      }
      
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (contractId, customerName) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/contracts/${contractId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrat_${customerName.replace(' ', '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF téléchargé');
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du téléchargement PDF');
    }
  };

  const handleDownloadWord = async (contractId, customerName) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/contracts/${contractId}/docx`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrat_${customerName.replace(' ', '_')}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Document Word téléchargé');
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du téléchargement Word');
    }
  };

  const handleEditContract = (contractId) => {
    navigate(`/admin/contracts/create?contract=${contractId}`);
  };

  const handleDeleteContract = async (contractId, customerName) => {
    const confirmed = window.confirm(
      `Êtes-vous sûr de vouloir supprimer le contrat de ${customerName} ?\n\nCette action est irréversible.`
    );
    
    if (!confirmed) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/contracts/${contractId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Retirer le contrat de la liste localement
      setContracts(contracts.filter(c => c.id !== contractId));
      
      toast.success('Contrat supprimé avec succès');
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors de la suppression du contrat');
    }
  };

  const handleStatusChange = async (contractId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(`${API}/contracts/${contractId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Mettre à jour la liste localement
      setContracts(contracts.map(c => 
        c.id === contractId ? { ...c, status: newStatus } : c
      ));
      
      const statusLabels = {
        'draft': 'Brouillon',
        'signed': 'Signé',
        'cancelled': 'Annulé'
      };
      
      toast.success(`Statut changé en "${statusLabels[newStatus]}"`);
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du changement de statut');
    }
  };

  // Filter contracts based on search and filters
  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = contractSearch === '' || 
      contract.customer.name.toLowerCase().includes(contractSearch.toLowerCase()) ||
      contract.customer.email.toLowerCase().includes(contractSearch.toLowerCase()) ||
      contract.project.model.toLowerCase().includes(contractSearch.toLowerCase());
    
    const matchesDate = contractDateFilter === '' ||
      contract.contract_date.includes(contractDateFilter);
    
    const matchesModel = contractModelFilter === '' ||
      contract.project.model.toLowerCase().includes(contractModelFilter.toLowerCase());
    
    return matchesSearch && matchesDate && matchesModel;
  });

  // Filter submissions based on search and filters
  const filteredSubmissions = submissions.filter(sub => {
    const matchesSearch = submissionSearch === '' || 
      sub.customer.name.toLowerCase().includes(submissionSearch.toLowerCase()) ||
      sub.customer.email.toLowerCase().includes(submissionSearch.toLowerCase()) ||
      sub.project.model.toLowerCase().includes(submissionSearch.toLowerCase());
    
    const matchesDate = submissionDateFilter === '' ||
      new Date(sub.created_at).toLocaleDateString('fr-CA').includes(submissionDateFilter);
    
    const matchesModel = submissionModelFilter === '' ||
      sub.project.model.toLowerCase().includes(submissionModelFilter.toLowerCase());
    
    return matchesSearch && matchesDate && matchesModel;
  });

  const handleSelectSubmission = (submission) => {
    // Rediriger vers le formulaire de création avec l'ID de la soumission
    navigate(`/admin/contracts/create?submission=${submission.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#335C2E] mx-auto"></div>
          <p className="mt-4">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-200 rounded"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-3xl font-bold" style={{ color: '#335C2E' }}>
              Contrats de vente
            </h1>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 px-4 py-2 text-white rounded"
            style={{ background: '#C7A027' }}
          >
            <Plus size={20} />
            Créer un contrat
          </button>
        </div>

        {/* Liste des contrats existants */}
        {!showCreateForm && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4" style={{ color: '#335C2E' }}>
              Contrats existants ({filteredContracts.length})
            </h2>

            {/* Search and Filter Section */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border" style={{ borderColor: '#E5E5E5' }}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#333333' }}>
                    🔍 Recherche (nom, email, modèle)
                  </label>
                  <input
                    type="text"
                    value={contractSearch}
                    onChange={(e) => setContractSearch(e.target.value)}
                    placeholder="Rechercher..."
                    className="w-full p-2 border rounded-lg"
                    style={{ borderColor: '#E5E5E5' }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#333333' }}>
                    📅 Filtrer par date
                  </label>
                  <input
                    type="date"
                    value={contractDateFilter}
                    onChange={(e) => setContractDateFilter(e.target.value)}
                    className="w-full p-2 border rounded-lg"
                    style={{ borderColor: '#E5E5E5' }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#333333' }}>
                    🏠 Filtrer par modèle
                  </label>
                  <input
                    type="text"
                    value={contractModelFilter}
                    onChange={(e) => setContractModelFilter(e.target.value)}
                    placeholder="Ex: UHA, Gazébo..."
                    className="w-full p-2 border rounded-lg"
                    style={{ borderColor: '#E5E5E5' }}
                  />
                </div>
              </div>
              
              {(contractSearch || contractDateFilter || contractModelFilter) && (
                <button
                  onClick={() => {
                    setContractSearch('');
                    setContractDateFilter('');
                    setContractModelFilter('');
                  }}
                  className="mt-3 text-sm px-3 py-1 rounded hover:bg-gray-200"
                  style={{ color: '#335C2E' }}
                >
                  ✕ Réinitialiser les filtres
                </button>
              )}
            </div>
            
            {filteredContracts.length === 0 && contracts.length > 0 ? (
              <p className="text-gray-500">Aucun contrat ne correspond aux filtres.</p>
            ) : filteredContracts.length === 0 ? (
              <p className="text-gray-500">Aucun contrat créé pour le moment.</p>
            ) : (
              <div className="overflow-x-auto" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                <table className="w-full">
                  <thead className="sticky top-0" style={{ background: '#F0F7F4', zIndex: 10 }}>
                    <tr>
                      <th className="p-3 text-left">Date</th>
                      <th className="p-3 text-left">Client</th>
                      <th className="p-3 text-left">Projet</th>
                      <th className="p-3 text-left">Montant</th>
                      <th className="p-3 text-left">Statut</th>
                      <th className="p-3 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContracts.map((contract) => (
                      <tr key={contract.id} className="border-b hover:bg-gray-50">
                        <td className="p-3">{contract.contract_date}</td>
                        <td className="p-3">{contract.customer.name}</td>
                        <td className="p-3">{contract.project.model}</td>
                        <td className="p-3">{contract.calculation.total_ttc.toLocaleString()} $</td>
                        <td className="p-3">
                          <select
                            value={contract.status || 'draft'}
                            onChange={(e) => handleStatusChange(contract.id, e.target.value)}
                            className="px-2 py-1 rounded text-sm border cursor-pointer"
                            style={{
                              background: contract.status === 'signed' ? '#C7F0C7' : contract.status === 'cancelled' ? '#FFE0E0' : '#FFF4CC',
                              color: contract.status === 'signed' ? '#2D5016' : contract.status === 'cancelled' ? '#8B1414' : '#8B6914',
                              borderColor: contract.status === 'signed' ? '#81C784' : contract.status === 'cancelled' ? '#EF5350' : '#FFD54F'
                            }}
                          >
                            <option value="draft">📝 Brouillon</option>
                            <option value="signed">✅ Signé</option>
                            <option value="cancelled">❌ Annulé</option>
                          </select>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1 justify-center">
                            <button
                              onClick={() => handleDownloadPDF(contract.id, contract.customer.name)}
                              className="p-2 rounded hover:bg-green-100 text-lg"
                              title="Télécharger PDF"
                            >
                              📄
                            </button>
                            <button
                              onClick={() => handleDownloadWord(contract.id, contract.customer.name)}
                              className="p-2 rounded hover:bg-yellow-100 text-lg"
                              title="Télécharger Word"
                            >
                              📝
                            </button>
                            <button
                              onClick={() => handleEditContract(contract.id)}
                              className="p-2 rounded hover:bg-blue-100 text-lg"
                              title="Modifier le contrat"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleDeleteContract(contract.id, contract.customer.name)}
                              className="p-2 rounded hover:bg-red-100 text-lg"
                              title="Supprimer le contrat"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Sélection de soumission */}
        {showCreateForm && !selectedSubmission && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold" style={{ color: '#335C2E' }}>
                Sélectionner une soumission ({filteredSubmissions.length})
              </h2>
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                Annuler
              </button>
            </div>

            {/* Search and Filter Section for Submissions */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border" style={{ borderColor: '#E5E5E5' }}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#333333' }}>
                    🔍 Recherche (nom, email, modèle)
                  </label>
                  <input
                    type="text"
                    value={submissionSearch}
                    onChange={(e) => setSubmissionSearch(e.target.value)}
                    placeholder="Rechercher..."
                    className="w-full p-2 border rounded-lg"
                    style={{ borderColor: '#E5E5E5' }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#333333' }}>
                    📅 Filtrer par date
                  </label>
                  <input
                    type="date"
                    value={submissionDateFilter}
                    onChange={(e) => setSubmissionDateFilter(e.target.value)}
                    className="w-full p-2 border rounded-lg"
                    style={{ borderColor: '#E5E5E5' }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#333333' }}>
                    🏠 Filtrer par modèle
                  </label>
                  <input
                    type="text"
                    value={submissionModelFilter}
                    onChange={(e) => setSubmissionModelFilter(e.target.value)}
                    placeholder="Ex: UHA, Gazébo..."
                    className="w-full p-2 border rounded-lg"
                    style={{ borderColor: '#E5E5E5' }}
                  />
                </div>
              </div>
              
              {(submissionSearch || submissionDateFilter || submissionModelFilter) && (
                <button
                  onClick={() => {
                    setSubmissionSearch('');
                    setSubmissionDateFilter('');
                    setSubmissionModelFilter('');
                  }}
                  className="mt-3 text-sm px-3 py-1 rounded hover:bg-gray-200"
                  style={{ color: '#335C2E' }}
                >
                  ✕ Réinitialiser les filtres
                </button>
              )}
            </div>
            
            <div className="overflow-x-auto" style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <table className="w-full">
                <thead className="sticky top-0" style={{ background: '#F0F7F4', zIndex: 10 }}>
                  <tr style={{ background: '#F0F7F4' }}>
                    <th className="p-3 text-left">Date</th>
                    <th className="p-3 text-left">Client</th>
                    <th className="p-3 text-left">Projet</th>
                    <th className="p-3 text-left">Options</th>
                    <th className="p-3 text-left">Montant</th>
                    <th className="p-3 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubmissions.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="p-3 text-center text-gray-500">
                        Aucune soumission ne correspond aux filtres
                      </td>
                    </tr>
                  ) : (
                    filteredSubmissions.map((submission) => (
                      <tr key={submission.id} className="border-b hover:bg-gray-50">
                        <td className="p-3">{new Date(submission.created_at).toLocaleDateString()}</td>
                        <td className="p-3">{submission.customer.name}</td>
                        <td className="p-3">{submission.project.model}</td>
                        <td className="p-3">
                          {submission.project.options && submission.project.options.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {submission.project.options.slice(0, 2).map((opt, idx) => (
                                <span key={idx} className="px-2 py-0.5 rounded text-xs" style={{ background: '#E8F5E9', color: '#2E7D32' }}>
                                  {opt}
                                </span>
                              ))}
                              {submission.project.options.length > 2 && (
                                <span className="px-2 py-0.5 rounded text-xs" style={{ background: '#FFF3E0', color: '#E65100' }}>
                                  +{submission.project.options.length - 2}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs italic text-gray-400">Aucune</span>
                          )}
                        </td>
                        <td className="p-3">{submission.calculation.total_ttc.toLocaleString()} $</td>
                        <td className="p-3">
                          <button
                            onClick={() => handleSelectSubmission(submission)}
                            className="px-3 py-1 text-white rounded text-sm"
                            style={{ background: '#C7A027' }}
                          >
                            Sélectionner
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Le formulaire de création est maintenant géré par ContractForm.jsx */}
      </div>
    </div>
  );
};

export default Contracts;
